/**
 * Who Are We Page
 * Detailed organizational overview with content from About Us CMS API
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Home,
  Landmark,
  Clock,
} from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const ABOUT_INFO_ID = 16;

/* ─── Hero ─── */
const WhoAreWeHero = () => (
  <section className="bg-[#2f3951] py-5 md:py-5">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <nav className="flex items-center text-white/80 text-sm mb-3">
        <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
          <Home className="w-4 h-4 mr-1" />
          Home
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <Link to="/about" className="hover:text-[#ca9a37] transition-colors">
          About Us
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-[#ca9a37]">Who Are We</span>
      </nav>

      <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
        Who Are We
      </h1>
      <div className="w-24 h-1 bg-[#ca9a37] mb-3"></div>
      <p className="text-base text-gray-200 max-w-2xl">
        Discover the story, mission, and values behind Uhamiaji SACCOS.
      </p>
    </div>
  </section>
);

/* ─── Organization Overview ─── */
const OrganizationOverview = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: ABOUT_INFO_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setContent({
              Title: data.Title,
              Description: data.Description,
              Image: data.Image,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch about content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Loading skeleton
  if (loading) {
    return (
      <section className="pt-7 pb-0 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="mb-6">
            <div className="h-10 w-48 bg-gray-200 rounded mb-6 animate-pulse" />
            <div className="w-16 h-1 bg-gray-200 mb-6 animate-pulse" />
          </div>
          <div className="bg-gray-100 h-80 md:h-[26rem] lg:h-[30rem] rounded-2xl mb-14 animate-pulse" />
          <div className="space-y-4">
            <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  // Return null if no data from API after loading
  if (!content) {
    return null;
  }

  const paragraphs = (content.Description || '')
    .replace(/<[^>]*>/g, '\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .split('\n')
    .filter(p => p.trim().length > 0)
    .map(p => p.trim());

  return (
    <section className="pt-7 pb-0 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-6">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2f3951] mb-6">
            {content.Title || 'Who We Are'}
          </h2>
          <div className="w-16 h-1 bg-[#ca9a37] mb-6"></div>
        </div>

        {/* Image */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl mb-14">
          {content.Image ? (
            <img
              src={content.Image}
              alt={content.Title || 'Organization'}
              className="w-full h-80 md:h-[26rem] lg:h-[30rem] object-cover"
            />
          ) : (
            <div className="bg-gray-100 h-80 md:h-[26rem] lg:h-[30rem] flex items-center justify-center">
              <div className="text-center">
                <Landmark className="w-24 h-24 text-[#ca9a37]/30 mx-auto mb-4" />
                <p className="text-gray-400 font-medium">Organization Image</p>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div>
          {paragraphs.length > 0 && (
            <p className="text-lg md:text-xl text-[#2f3951] font-medium leading-relaxed mb-6 text-justify">
              {paragraphs[0]}
            </p>
          )}

          {paragraphs.length > 1 && (
            <div className="border-l-4 border-[#ca9a37] pl-6 md:pl-8">
              {paragraphs.slice(1).map((paragraph, index) => (
                <p
                  key={index}
                  className={`text-gray-600 leading-relaxed text-justify ${index < paragraphs.slice(1).length - 1 ? 'mb-5' : ''}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ─── Our Journey (Timeline) ─── */
const OurJourney = () => {
  const milestones = [
    { year: '2010', title: 'Established', desc: 'Uhamiaji SACCOS was founded with a mission to serve members.' },
    { year: '2015', title: '1,000 Members', desc: 'Reached a significant milestone of 1,000 active members.' },
    { year: '2020', title: 'Digital Transformation', desc: 'Launched online services and digital member portal.' },
    { year: '2025', title: 'Nationwide Expansion', desc: 'Expanded services and branch network across the country.' },
  ];

  return (
    <section className="py-14 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-10">
          <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">History</span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mt-2 mb-4">Our Journey</h2>
          <div className="w-16 h-1 bg-[#ca9a37] mx-auto"></div>
        </div>

        <div className="relative max-w-3xl mx-auto">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#ca9a37]/30 transform md:-translate-x-0.5"></div>

          {milestones.map((m, i) => (
            <div key={i} className={`relative flex items-start mb-8 last:mb-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
              {/* Dot */}
              <div className="absolute left-6 md:left-1/2 w-3 h-3 bg-[#ca9a37] rounded-full transform -translate-x-1.5 mt-2 z-10"></div>

              {/* Content */}
              <div className={`ml-14 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 inline-block">
                  <span className="text-[#ca9a37] font-bold text-lg">{m.year}</span>
                  <h4 className="font-semibold text-[#2f3951] mt-1">{m.title}</h4>
                  <p className="text-gray-500 text-sm mt-1">{m.desc}</p>
                </div>
              </div>

              {/* Spacer for opposite side */}
              <div className="hidden md:block md:w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Page ─── */
const WhoAreWePage = () => (
  <div>
    <WhoAreWeHero />
    <OrganizationOverview />
    <OurJourney />
  </div>
);

export default WhoAreWePage;
